import { WORLD_VERSION } from '@cloudpearl/config';
import type { MapId } from '@cloudpearl/config';
import type { RunMode, RoomInfo } from '../ui/Ui';
import { Room } from './Room';
import type { NetState } from './RemotePearl';

/** Where a player is in the shared loop. */
export type Phase = 'loading' | 'menu' | 'countdown' | 'running' | 'results';

export interface SessionHooks {
  /** The local world (map + seed) and our pearl's colour. */
  config(): { map: MapId; seed: string; color: string };
  /** Load this world (map and/or seed) if it differs from the local one. */
  adopt(map: MapId, seed: string): void;
  /** Both ready: run the synced countdown, then start (from the menu or the results card). */
  go(): void;
  state(s: NetState): void;
  /** The friend's pearl colour. */
  look(color: string): void;
  /** The friend's pearl hit ours: apply this velocity change and separation. */
  bumped(dvx: number, dvy: number, dvz: number, px: number, pz: number): void;
  /** Room / friend / mode changed: refresh the UI. */
  changed(): void;
  /** The friend left (the session may live on, hosting); `closed` = the room is gone. */
  left(reason: string, closed: boolean): void;
}

type Msg =
  | { k: 'hello'; v: number; map: MapId; seed: string; mode: RunMode; p: Phase; c: string }
  | { k: 'look'; c: string }
  | { k: 'cfg'; map: MapId; seed: string; mode: RunMode }
  | { k: 'phase'; p: Phase; map: MapId; seed: string }
  | { k: 'go' }
  | { k: 'bump'; vx: number; vy: number; vz: number; px: number; pz: number }
  | NetState;

/**
 * A two-player shared run. Each client simulates only its own pearl (solo determinism is kept)
 * and streams snapshots of it; this layer keeps both players on the same world, gates the start
 * on both being ready, and relays bumps. The host's world wins any conflict.
 */
export class Session {
  readonly room: Room;
  mode: RunMode = 'race';
  status: RoomInfo['status'];
  private phase: Phase = 'menu';
  private friendPhase: Phase = 'loading';
  private friendMap: MapId | null = null;
  private friendSeed: string | null = null;
  private sendT = 0;

  constructor(
    private readonly hooks: SessionHooks,
    join?: string,
  ) {
    const events = {
      onHosting: () => {
        this.status = 'hosting';
        hooks.changed();
      },
      onConnected: () => {
        this.status = 'connected';
        this.friendPhase = 'loading';
        const c = hooks.config();
        this.room.send({ k: 'hello', v: WORLD_VERSION, map: c.map, seed: c.seed, mode: this.mode, p: this.phase, c: c.color });
        hooks.changed();
      },
      onMessage: (m: unknown) => this.onMessage(m as Msg),
      onFriendLeft: () => {
        this.status = 'hosting';
        this.friendPhase = 'loading';
        hooks.left('Your friend left', false);
        hooks.changed();
      },
      onClosed: (reason: string) => hooks.left(reason, true),
    };
    this.status = join ? 'connecting' : 'hosting';
    this.room = join ? Room.join(join, events) : Room.host(events);
  }

  get isHost(): boolean {
    return this.room.isHost;
  }

  get code(): string {
    return this.room.code;
  }

  get connected(): boolean {
    return this.status === 'connected';
  }

  /** One-way latency estimate (s). */
  get latency(): number {
    return this.room.rtt / 2000;
  }

  /** The friend is on the menu / results with the same world loaded. */
  get friendReady(): boolean {
    const c = this.hooks.config();
    return this.connected && (this.friendPhase === 'menu' || this.friendPhase === 'results') && this.friendMap === c.map && this.friendSeed === c.seed;
  }

  get friendRunning(): boolean {
    return this.connected && (this.friendPhase === 'running' || this.friendPhase === 'countdown');
  }

  info(): RoomInfo {
    return { code: this.code, status: this.status, mode: this.mode, friendReady: this.friendReady };
  }

  /** Local phase changes are broadcast so the friend knows when a shared start is possible. */
  setPhase(p: Phase): void {
    this.phase = p;
    const c = this.hooks.config();
    this.room.send({ k: 'phase', p, map: c.map, seed: c.seed });
    this.hooks.changed();
  }

  /** We changed the world or mode on our side: the friend follows. */
  pushConfig(): void {
    const c = this.hooks.config();
    this.room.send({ k: 'cfg', map: c.map, seed: c.seed, mode: this.mode });
    this.hooks.changed();
  }

  setMode(mode: RunMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.pushConfig();
  }

  /** Ask for a shared start; we start half a round trip later so both counts line up. */
  requestGo(): boolean {
    if (!this.friendReady) return false;
    this.room.send({ k: 'go' });
    setTimeout(() => this.hooks.go(), this.room.rtt / 2);
    return true;
  }

  /** Stream our pearl (~20 Hz while running, slower otherwise). */
  sendState(s: NetState, dt: number, urgent = false): void {
    this.sendT -= dt;
    if (!urgent && this.sendT > 0) return;
    this.sendT = s.r && s.a ? 0.05 : 0.25;
    this.room.send(s);
  }

  /** Our pearl changed colour. */
  setLook(color: string): void {
    this.room.send({ k: 'look', c: color });
  }

  bump(vx: number, vy: number, vz: number, px: number, pz: number): void {
    this.room.send({ k: 'bump', vx, vy, vz, px, pz });
  }

  leave(): void {
    this.room.leave();
  }

  private onMessage(m: Msg): void {
    switch (m.k) {
      case 'hello': {
        if (m.v !== WORLD_VERSION) {
          this.room.leave();
          this.hooks.left('Your friend has a different version of the game — both reload', true);
          return;
        }
        this.friendPhase = m.p;
        this.friendMap = m.map;
        this.friendSeed = m.seed;
        if (typeof m.c === 'string') this.hooks.look(m.c);
        // The guest takes the host's world and mode.
        if (!this.isHost) {
          this.mode = m.mode;
          this.hooks.adopt(m.map, m.seed);
        }
        this.hooks.changed();
        break;
      }
      case 'cfg':
        this.mode = m.mode;
        this.hooks.adopt(m.map, m.seed);
        this.hooks.changed();
        break;
      case 'phase': {
        this.friendPhase = m.p;
        this.friendMap = m.map;
        this.friendSeed = m.seed;
        // Crossed wires (both changed the world at once): the host re-asserts its world.
        const c = this.hooks.config();
        if (this.isHost && m.p === 'menu' && this.phase === 'menu' && (m.map !== c.map || m.seed !== c.seed)) this.pushConfig();
        this.hooks.changed();
        break;
      }
      case 'look':
        this.hooks.look(m.c);
        break;
      case 'go':
        if (this.phase === 'menu' || this.phase === 'results') this.hooks.go();
        break;
      case 'bump':
        this.hooks.bumped(m.vx, m.vy, m.vz, m.px, m.pz);
        break;
      case 's':
        this.hooks.state(m);
        break;
    }
  }
}
